import { Controller } from "@hotwired/stimulus"

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Connects to data-controller="ui--popover"
// Handles popover show/hide, positioning, and keyboard navigation
export default class extends Controller {
  static targets = ["trigger", "content", "backdrop"]
  static values = {
    dismissible: { type: Boolean, default: true },
  }

  connect() {
    // Listen for escape key
    this.escapeHandler = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.escapeHandler)

    // Tab trap within popover content
    this._tabHandler = this._handleTab.bind(this)
    this.element.addEventListener("keydown", this._tabHandler)
  }

  disconnect() {
    document.removeEventListener("keydown", this.escapeHandler)
    this.element.removeEventListener("keydown", this._tabHandler)
  }

  toggle(event) {
    event.stopPropagation()

    if (this.isOpen()) {
      this.hide()
    } else {
      this.show()
    }
  }

  show() {
    if (this.hasContentTarget) {
      this.contentTarget.classList.remove("hidden")

      if (this.dismissibleValue && this.hasBackdropTarget) {
        this.backdropTarget.classList.remove("hidden")
      }

      // Focus first focusable element inside popover content
      requestAnimationFrame(() => {
        const firstFocusable =
          this.contentTarget.querySelector(FOCUSABLE_SELECTOR)
        if (firstFocusable) {
          firstFocusable.focus()
        }
      })

      // Dispatch open event
      this.element.dispatchEvent(
        new CustomEvent("popover:open", {
          bubbles: true,
          detail: { popover: this.element },
        }),
      )
    }
  }

  hide() {
    if (this.hasContentTarget) {
      this.contentTarget.classList.add("hidden")

      if (this.hasBackdropTarget) {
        this.backdropTarget.classList.add("hidden")
      }

      // Return focus to trigger
      if (this.hasTriggerTarget) {
        const focusable =
          this.triggerTarget.querySelector(FOCUSABLE_SELECTOR) ||
          this.triggerTarget
        focusable.focus()
      }

      // Dispatch close event
      this.element.dispatchEvent(
        new CustomEvent("popover:close", {
          bubbles: true,
          detail: { popover: this.element },
        }),
      )
    }
  }

  handleEscape(event) {
    if (event.key === "Escape" && this.isOpen()) {
      this.hide()
    }
  }

  // When Tab reaches the last focusable element in the popover, close it
  _handleTab(event) {
    if (event.key !== "Tab" || !this.isOpen() || !this.hasContentTarget) return

    const focusableElements = Array.from(
      this.contentTarget.querySelectorAll(FOCUSABLE_SELECTOR),
    )
    if (focusableElements.length === 0) return

    const lastFocusable = focusableElements[focusableElements.length - 1]
    const firstFocusable = focusableElements[0]

    if (event.shiftKey && document.activeElement === firstFocusable) {
      // Shift+Tab on first element: close popover and return focus to trigger
      event.preventDefault()
      this.hide()
    } else if (!event.shiftKey && document.activeElement === lastFocusable) {
      // Tab on last element: close popover and let focus move naturally
      event.preventDefault()
      this.hide()
    }
  }

  isOpen() {
    return (
      this.hasContentTarget && !this.contentTarget.classList.contains("hidden")
    )
  }
}
